import React, { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { register, isAuthenticated, errorCode } from 'services/auth.service'
import AuthLayout, { authInputClass, authLabelClass, authButtonClass } from 'components/AuthLayout'
import PasswordStrength, { MIN_PASSWORD } from 'components/PasswordStrength'
import { legalUrl } from 'config'
import { useI18n, apiError } from 'i18n'

// The website links here with ?lang=es|en so the app opens in the same language.
const LANG_PARAM = { es: 'es-MX', en: 'en-US' }

const Signup = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { t, locale, setLocale } = useI18n()
  const [form, setForm] = useState({ firstName: '', lastName: '', companyName: '', email: '', password: '' })
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [busy, setBusy] = useState(false)
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  useEffect(() => {
    const lang = LANG_PARAM[params.get('lang')]
    if (lang) setLocale(lang)
  }, [params, setLocale])

  if (isAuthenticated()) return <Navigate to='/' replace />

  const canSubmit = acceptTerms && form.password.length >= MIN_PASSWORD && !busy

  const onSubmit = async (event) => {
    event.preventDefault()
    if (!canSubmit) return
    setBusy(true)
    try {
      const { email } = await register({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        companyName: form.companyName.trim(),
        email: form.email.trim(),
        password: form.password,
        acceptTerms
      })
      navigate(`/verify-email?email=${encodeURIComponent(email)}`)
    } catch (error) {
      if (errorCode(error) === 'EMAIL_TAKEN') {
        toast.error(t('signup.emailTaken'))
        return
      }
      toast.error(apiError(error, t('signup.failed')))
    } finally {
      setBusy(false)
    }
  }

  const legalLink = (doc) => (
    <a href={legalUrl(doc, locale)} target='_blank' rel='noreferrer' className='font-semibold text-brand hover:underline'>
      {t(`signup.${doc}`)}
    </a>
  )

  return (
    <AuthLayout
      wide
      subtitle={t('signup.subtitle')}
      onSubmit={onSubmit}
      footer={<>{t('signup.haveAccount')} <Link to='/login' className='font-semibold text-accent hover:underline'>{t('signup.signIn')}</Link></>}
    >
      <ul className='mb-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs font-medium text-slate-600'>
        {['perkShipments', 'perkCard', 'perkSetup'].map((key) => (
          <li key={key} className='flex items-center gap-1'>
            <svg aria-hidden viewBox='0 0 20 20' className='h-4 w-4 text-status-ready' fill='currentColor'>
              <path fillRule='evenodd' d='M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z' clipRule='evenodd' />
            </svg>
            {t(`signup.${key}`)}
          </li>
        ))}
      </ul>

      <div className='mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2'>
        <div>
          <label htmlFor='su-first' className={authLabelClass}>{t('signup.firstName')}</label>
          <input id='su-first' required autoComplete='given-name' value={form.firstName} onChange={set('firstName')} className={authInputClass} />
        </div>
        <div>
          <label htmlFor='su-last' className={authLabelClass}>{t('signup.lastName')}</label>
          <input id='su-last' required autoComplete='family-name' value={form.lastName} onChange={set('lastName')} className={authInputClass} />
        </div>
      </div>

      <label htmlFor='su-company' className={authLabelClass}>{t('signup.company')}</label>
      <input id='su-company' required minLength={2} autoComplete='organization' value={form.companyName} onChange={set('companyName')}
        className={`mb-4 ${authInputClass}`} />

      <label htmlFor='su-email' className={authLabelClass}>{t('signup.email')}</label>
      <input id='su-email' type='email' required autoComplete='email' value={form.email} onChange={set('email')}
        aria-describedby='su-email-hint' className={authInputClass} />
      <p id='su-email-hint' className='mb-4 mt-1 text-xs text-slate-500'>{t('signup.emailHint')}</p>

      <label htmlFor='su-password' className={authLabelClass}>{t('signup.password')}</label>
      <input id='su-password' type='password' required minLength={MIN_PASSWORD} autoComplete='new-password' value={form.password} onChange={set('password')}
        aria-describedby='su-password-hint' className={authInputClass} />
      <PasswordStrength id='su-password-hint' value={form.password} />

      <label className='mb-6 flex items-start gap-2 text-sm text-slate-600'>
        <input type='checkbox' required checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
          className='mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-brand' />
        <span>
          {t('signup.acceptPrefix')} {legalLink('terms')} {t('signup.and')} {legalLink('privacy')}.
        </span>
      </label>

      <button type='submit' disabled={!canSubmit} className={authButtonClass}>
        {busy ? t('signup.submitting') : t('signup.submit')}
      </button>
    </AuthLayout>
  )
}

export default Signup
